/**
 * shouldCost.js — Within-cohort regression should-cost model (Phase 4).
 *
 * Model (demeaned within cohort, pooled globally):
 *   ln(uc) − mean_coh(ln uc) = Σ β_k · (x_k − mean_coh(x_k)) + ε
 *
 * Features x_k, in this order:
 *   1. ln(eau + 1)            (volume)
 *   2. complexity             (0–100 score)
 *   3. ln(tightest_mm)        (0 when missing/<=0)
 *   4. setup_count            (0 when missing)
 *   5. ln(buy_to_fly + 1)     (0 when missing)
 *
 * Fit: OLS via normal equations (5×5 — solved with Gaussian elimination, no deps).
 * Only cohorts with >= 5 priced entries contribute. Demeaning within cohort
 * makes missing-value zeros act as mean imputation — acceptable.
 */

const N_FEATURES = 5

/** Extract feature vector for one priced entry */
function extractFeatures(entry) {
  const eau = parseFloat(entry.eau) || 0
  const complexity = Number(entry.complexity) || 0
  const tightest = Number(entry.tightest_mm) || 0
  const setups = Number(entry.setup_count) || 0
  const btf = Number(entry.buy_to_fly) || 0

  return [
    eau > 0 ? Math.log(eau + 1) : 0,
    complexity,
    tightest > 0 ? Math.log(tightest) : 0,
    setups,
    btf > 0 ? Math.log(btf + 1) : 0,
  ]
}

/** Solve Ax = b via Gaussian elimination with partial pivoting (k×k system) */
function solveLinear(A, b) {
  const n = b.length
  // Augmented matrix
  const M = A.map((row, i) => [...row, b[i]])

  for (let col = 0; col < n; col++) {
    // Partial pivot
    let maxRow = col
    let maxVal = Math.abs(M[col][col])
    for (let row = col + 1; row < n; row++) {
      if (Math.abs(M[row][col]) > maxVal) {
        maxVal = Math.abs(M[row][col])
        maxRow = row
      }
    }
    if (maxVal < 1e-12) return null // singular
    if (maxRow !== col) { const tmp = M[col]; M[col] = M[maxRow]; M[maxRow] = tmp }

    // Eliminate below
    for (let row = col + 1; row < n; row++) {
      const factor = M[row][col] / M[col][col]
      for (let j = col; j <= n; j++) M[row][j] -= factor * M[col][j]
    }
  }

  // Back-substitute
  const x = new Array(n).fill(0)
  for (let i = n - 1; i >= 0; i--) {
    let sum = M[i][n]
    for (let j = i + 1; j < n; j++) sum -= M[i][j] * x[j]
    if (Math.abs(M[i][i]) < 1e-12) return null
    x[i] = sum / M[i][i]
  }
  return x
}

/**
 * Fit the within-cohort regression should-cost model.
 *
 * @param {Array} parts - enriched parts array (from computePricePositions)
 * @param {Function} buildCohortKey - cohort key builder function
 * @returns {Object|null} { beta, r2, sigma, n, predict, cohortMeans } or null if insufficient data
 */
export function fitShouldCost(parts, buildCohortKey) {
  // 1. Group entries by cohort
  const cohortEntries = {}

  parts.forEach(p => {
    const coh = buildCohortKey(p)
    if (!coh) return
    ;(p.suppliers || []).forEach(s => {
      const uc = Number(s.unit_cost) || 0
      if (uc <= 0) return
      if (!cohortEntries[coh]) cohortEntries[coh] = []
      cohortEntries[coh].push({
        luc: Math.log(uc),
        feats: extractFeatures({ ...p, eau: s.eau }),
        pn: p.pn,
        supplier: s.name,
      })
    })
  })

  // 2. Compute per-cohort means, filter cohorts with >= 5 entries
  const cohortMeans = {}
  const allDemeaned = [] // { dluc, dfeats }

  Object.entries(cohortEntries).forEach(([coh, entries]) => {
    if (entries.length < 5) return

    const n = entries.length
    const meanLuc = entries.reduce((s, e) => s + e.luc, 0) / n
    const meanFeats = new Array(N_FEATURES).fill(0)
    entries.forEach(e => { for (let k = 0; k < N_FEATURES; k++) meanFeats[k] += e.feats[k] })
    for (let k = 0; k < N_FEATURES; k++) meanFeats[k] /= n

    cohortMeans[coh] = { luc: meanLuc, feats: meanFeats, n }

    entries.forEach(e => {
      const dluc = e.luc - meanLuc
      const dfeats = e.feats.map((f, k) => f - meanFeats[k])
      allDemeaned.push({ dluc, dfeats })
    })
  })

  const totalN = allDemeaned.length
  if (totalN < 200) return null // insufficient data

  // 3. Build normal equations: X'X β = X'y
  // X is totalN × N_FEATURES (demeaned features), y is totalN × 1 (demeaned ln uc)
  const XtX = Array.from({ length: N_FEATURES }, () => new Array(N_FEATURES).fill(0))
  const Xty = new Array(N_FEATURES).fill(0)
  let yty = 0

  allDemeaned.forEach(({ dluc, dfeats }) => {
    for (let i = 0; i < N_FEATURES; i++) {
      Xty[i] += dfeats[i] * dluc
      for (let j = 0; j < N_FEATURES; j++) {
        XtX[i][j] += dfeats[i] * dfeats[j]
      }
    }
    yty += dluc * dluc
  })

  // 4. Solve
  const beta = solveLinear(XtX, Xty)
  if (!beta) return null

  // 5. Compute R² and sigma
  // SSres = yty - β'X'y
  let ssExplained = 0
  for (let k = 0; k < N_FEATURES; k++) ssExplained += beta[k] * Xty[k]
  const ssRes = yty - ssExplained
  const r2 = yty > 0 ? ssExplained / yty : 0
  const sigma = totalN > N_FEATURES ? Math.sqrt(ssRes / (totalN - N_FEATURES)) : Math.sqrt(ssRes / totalN)

  // Guard: if R² < 0.1, model is too weak
  if (r2 < 0.1) return null

  // 6. Predict function
  function predict(entry, cohortKey) {
    const cm = cohortMeans[cohortKey]
    if (!cm) return null
    const feats = extractFeatures(entry)
    let lnPred = cm.luc
    for (let k = 0; k < N_FEATURES; k++) {
      lnPred += beta[k] * (feats[k] - cm.feats[k])
    }
    return Math.exp(lnPred)
  }

  return { beta, r2, sigma, n: totalN, predict, cohortMeans }
}
